import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuthStore } from '../stores/auth';
import { MarkdownEditor } from '../components/MarkdownEditor';
import { LabelWithTooltip } from '../components/Tooltip';
import { useAutoSave, useLoadDraft, useClearDraft } from '../hooks/useAutoSave';
import { tooltips } from '../config/tooltips';
import { IconWithTooltip } from '../components/IconWithTooltip';
import { FloatingActions } from '../components/FloatingActions';
import { ConfirmModal } from '../components/ConfirmModal';

const NEW_ARTICLE_ID = 'new-article';

// Format tags: ensure each tag starts with #
function formatTags(input: string): string {
  if (!input.trim()) return '';

  // Split by comma or space, filter empty, add # if missing
  const tags = input
    .split(/[,\s]+/)
    .filter(tag => tag.trim())
    .map(tag => {
      const cleaned = tag.trim().replace(/^#+/, ''); // Remove existing #
      return cleaned ? `#${cleaned}` : '';
    })
    .filter(Boolean);

  return tags.join(' ');
}

type ContentLanguage = 'en' | 'zh';

export function Write() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();

  const [title, setTitle] = useState('');
  const [titleZh, setTitleZh] = useState('');
  const [content, setContent] = useState('');
  const [contentZh, setContentZh] = useState('');
  const [slug, setSlug] = useState('');
  const [tags, setTags] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private' | 'unlisted'>('public');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [contentLang, setContentLang] = useState<ContentLanguage>('en');

  const draft = useLoadDraft(NEW_ARTICLE_ID);
  const clearDraft = useClearDraft(NEW_ARTICLE_ID);

  // Load draft on mount
  useEffect(() => {
    if (draft) {
      setTitle(draft.title);
      setTitleZh(draft.titleZh || '');
      setContent(draft.content);
      setContentZh(draft.contentZh || '');
      setSlug(draft.slug);
      setTags(draft.tags);
      setVisibility(draft.visibility);
    }
  }, []);

  // Auto-save draft
  useAutoSave(NEW_ARTICLE_ID, { title, titleZh, content, contentZh, slug, tags, visibility });

  // Redirect if not authenticated or not admin
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/');
    }
  }, [isAuthenticated, user, navigate]);

  const handleTagsBlur = () => {
    setTags(formatTags(tags));
  };

  const handleSave = async (status: 'draft' | 'published') => {
    if (!title.trim() || !content.trim()) {
      alert('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const formattedTags = formatTags(tags);
      const { article } = await api.createArticle({
        title,
        title_zh: titleZh || undefined,
        content,
        content_zh: contentZh || undefined,
        slug: slug || undefined,
        tags: formattedTags,
        visibility,
        status,
      });

      clearDraft();
      navigate(`/article/${article.slug}`);
    } catch (error) {
      console.error('Failed to save article:', error);
      alert('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const updateLastSaved = useCallback(() => {
    setLastSaved(new Date());
  }, []);

  useEffect(() => {
    const interval = setInterval(updateLastSaved, 5000);
    return () => clearInterval(interval);
  }, [updateLastSaved, title, content, tags]);

  const handleDiscard = () => {
    clearDraft();
    setTitle('');
    setTitleZh('');
    setContent('');
    setContentZh('');
    setSlug('');
    setTags('');
    setVisibility('public');
    setShowDiscardModal(false);
    navigate('/');
  };

  if (!isAuthenticated || user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Write Article</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Create and publish your content</p>
        </div>
        {lastSaved && (
          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Draft saved
          </div>
        )}
      </div>

      {/* Floating Action Buttons */}
      <FloatingActions storageKey="write-actions-position">
        <IconWithTooltip
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          }
          tooltip={tooltips.discardChanges}
          onClick={() => setShowDiscardModal(true)}
          disabled={saving}
          variant="secondary"
        />
        <IconWithTooltip
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
          }
          tooltip={tooltips.saveDraft}
          onClick={() => handleSave('draft')}
          disabled={saving}
          variant="secondary"
        />
        <IconWithTooltip
          icon={
            saving ? (
              <div className="spinner w-5 h-5" />
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            )
          }
          tooltip={tooltips.publish}
          onClick={() => handleSave('published')}
          disabled={saving}
          variant="primary"
        />
      </FloatingActions>

      <div className="space-y-6">
        {/* Title */}
        <div className="card p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title (English)
              </label>
              <input
                type="text"
                placeholder="Enter a compelling title for your article..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full text-2xl font-semibold bg-transparent border-0 p-0 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:ring-0 focus:outline-none"
              />
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title (中文) <span className="text-gray-400 font-normal">- Optional</span>
              </label>
              <input
                type="text"
                placeholder="输入中文标题..."
                value={titleZh}
                onChange={(e) => setTitleZh(e.target.value)}
                className="w-full text-xl font-semibold bg-transparent border-0 p-0 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100 focus:ring-0 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="card p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Article Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <LabelWithTooltip
                label="URL Slug"
                tooltip="Custom URL path for your article. Leave empty to auto-generate from title."
                htmlFor="slug"
              />
              <input
                id="slug"
                type="text"
                placeholder="my-awesome-article"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                className="input text-sm"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">yoursite.com/article/<span className="text-gray-600 dark:text-gray-400">{slug || 'auto-generated'}</span></p>
            </div>
            <div>
              <LabelWithTooltip
                label="Tags"
                tooltip="Add tags to categorize your article. Separate with commas or spaces."
                htmlFor="tags"
              />
              <input
                id="tags"
                type="text"
                placeholder="tech, blog, tutorial"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                onBlur={handleTagsBlur}
                className="input text-sm"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">Separate with commas or spaces</p>
            </div>
            <div>
              <LabelWithTooltip
                label="Visibility"
                tooltip="Public: visible to everyone. Unlisted: accessible via link but not shown in lists. Private: only visible to you."
                htmlFor="visibility"
              />
              <select
                id="visibility"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as typeof visibility)}
                className="input text-sm"
              >
                <option value="public">Public - Visible to everyone</option>
                <option value="unlisted">Unlisted - Hidden from lists</option>
                <option value="private">Private - Only you can see</option>
              </select>
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="card overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-3 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Content</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Write in Markdown. Drag & drop or paste images directly.</p>
              </div>
              <div className="flex items-center gap-1 bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setContentLang('en')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    contentLang === 'en'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setContentLang('zh')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    contentLang === 'zh'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  中文 {!contentZh && <span className="text-gray-400 text-xs">(empty)</span>}
                </button>
              </div>
            </div>
          </div>
          <div className="p-4">
            {contentLang === 'en' ? (
              <MarkdownEditor
                value={content}
                onChange={setContent}
              />
            ) : (
              <MarkdownEditor
                value={contentZh}
                onChange={setContentZh}
              />
            )}
          </div>
        </div>
      </div>

      {/* Discard Changes Modal */}
      <ConfirmModal
        isOpen={showDiscardModal}
        title="Discard Changes"
        message="Are you sure you want to discard all changes? Any unsaved work will be lost."
        confirmText="Discard"
        cancelText="Keep Writing"
        variant="warning"
        onConfirm={handleDiscard}
        onCancel={() => setShowDiscardModal(false)}
      />
    </div>
  );
}
